/* lab-proof-confirm.js - v2.9.2 (17 Sep 2026)
   ---------------------------------------------------------------------------
   v2.9.2 (Thomas's 19:00 review of the Stickiply Studio preview):
     - R7: an empty add-a-design cell takes a green boundary and a light green
       fill on hover, in both modes.
     - R8: the edit (pencil) control is a yellow tile like the red remove tile:
       light #fdf2d7 with a #c07d00 ring and a #9c6605 glyph; dark #2b2416 with
       a 2px #ffc63c outline and a #ffc63c glyph.
     - R9: ONE line under the progress bar. While the bar is on, every status
       message is written into the bar's caption and the status line stays
       empty; an error turns the bar off and takes the status line.
     - R10: the success card is ALWAYS light (the storefront has no dark mode),
       carries the order summary (thumbnails, the option tiles, total and per
       piece) from o.doneSummary(), and CLOSE / VIEW CART call o.onDoneClose so
       the lab can close itself; ADD MORE DESIGNS leaves it open.
   ---------------------------------------------------------------------------
   v2.6 (Thomas's notes on v2.5, 17:25 to 19:08). The public API is unchanged
   and every option is still optional, so the other labs' open() calls work
   as before:
     - DOWNLOAD FILES left and ADD TO CART right on ONE row under the rights
       tick, equal heights (only when onDownload is passed; otherwise the
       full-width button as before).
     - the detail tiles are one hairline strip: icon + label + value per item.
     - the quarter number is a 30px green circle; the add-a-design control a
       48px green circle, centred.
     - every tile's controls on one line: eye / pencil / red x / minus, number,
       plus, all 34px, centred; the row wraps to two centred lines where a cell
       is too narrow, and never touches the cell edge.
     - dark: the primary buttons (the cloned Add to cart, View cart) are the
       light tile with dark ink; every control background is at least 3:1
       against its surface; no browser focus ring, the brand yellow on
       :focus-visible; one even scrim.
     - one design (Custom Magnets, max_designs 1): a single proof at the
       window's full width.
   ---------------------------------------------------------------------------
   The last step every lab takes when it is opened FROM A PRODUCT PAGE:
   the proof on screen, one confirm, then Add to cart. No download step.

   Thomas, 12 Sep 2026: "the add to cart, should be the same styling as my
   product pages and nobody is going to see it under downloads, nor do you need
   downloads if going from the product pages.. all you need is the proof to be
   shown on screen to confirm."

   v2.4 answers six things he said on v2.3:
     - VIEW / EDIT / REMOVE are ALWAYS on every proof tile, never behind a tap,
       and each tile carries that design's own quantity stepper.
     - The grid is ALWAYS four quarters. A filled quarter is a proof; an empty
       quarter is an add-a-design tile, centred, drawn like the slot strip's
       empty slot. So there is no "Add more designs" button anywhere: with four
       designs there are four proofs and nothing else.
     - The download button is the LIVE proof-lab page's download group: a green
       tinted full-width tile, icon in a rounded white-ish square, Sapiens title
       with a small caption. Values copied from assets/proof-lab.css .dl--zip.
     - After a successful add the window's CONTENT IS REPLACED: tick, one title,
       one line, then View cart / Close / Add more designs in the cart drawer's
       own button styles. No proofs, no option tiles, no title block, no link.
     - "Back to editing" is gone. The red x and EDIT are the ways back.
     - One gap value, LPC_GAP, between every block; the last element sits the
       same distance from the bottom edge as the title does from the top.

   Why this lives in the LIGHT DOM: Proof Lab (and Sticker Sheet Lab) render
   inside a shadow root, where the theme's own stylesheet does not reach. The
   only way to show the product page's real buy button with no restyling is to
   CLONE the live button node and keep it in the light DOM, where its own CSS
   still applies. Measured on the copy 12 Sep: the product page button is
   .product-form__submit.button.button--full-width.button--secondary.stkb
   (49px, 10px radius, Sapiens 15px, cart icon then label). Nothing here sets a
   colour, a radius or a size on it - it is his button, moved.

   Usage:
     window.LabProofConfirm.open({
       title, lead,
       slots: [ {n:1,id:"0",src,caption,qty} | {n:2,empty:true}, ... ],  // always 4
       rows:  [[label, value]],
       confirmText, addLabel,
       downloadLabel, downloadNote, onDownload,
       onEditDesign(id), onRemoveDesign(id) -> {slots,rows,canAddMore},
       onQty(id, qty)  -> {slots,rows,canAddMore},
       onAddSlot(n),                                   // an empty quarter
       onAdd(setStatus), doneText | doneText(), canAddMore, onAddMoreDone, onBack
     });
   The promise from onAdd owns the network work; this overlay owns the confirm,
   the status line and the disabled state, and nothing else.
*/
(function () {
  /* i18n (P1, 22 Sep 2026): the words below come from the stk-i18n-studio JSON island that
     sections/proof-lab-order.liquid renders; each English fallback is the text this file
     carried before. [name] placeholders (passed through by the island) are filled by fmt. */
  var stkT = window.stkT || function (k, f) { return f; };

  /* P240 ARTWORK CHECK (1 Nov 2026, approved by Thomas). The config is printed by
     snippets/stk-artwork-check.liquid on the product templates only; no config, no tile
     and no fee (fails closed). Unticked on every open. The state is snapshotted when Add
     to cart is pressed (acArmed) and handed to the Studio's onAdd as its second argument;
     withCheck() gives each design line its own check line {_check_for} in the SAME
     /cart/add.js request and stamps the design line {_check_id}; dropChecks() removes the
     paired check lines when a Studio rolls its design line back. */
  var ACFG, acOn = false, acArmed = false;
  function acCfg() {
    if (ACFG !== undefined) return ACFG;
    ACFG = null;
    try {
      var n = document.getElementById("stk-artwork-check-config");
      var c = n ? JSON.parse(n.textContent || "{}") : null;
      if (c && c.variantId && c.t) ACFG = c;
    } catch (e) { ACFG = null; }
    return ACFG;
  }
  function acToken() { return "ac" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }
  function acLines(items, on) {
    var c = acCfg(), want = on == null ? acArmed : !!on;
    if (!c || !want || items == null) return items;
    var out = [];
    (Array.isArray(items) ? items : [items]).forEach(function (it) {
      if (!it) return;
      var tok = acToken(), p = {}, k;
      for (k in (it.properties || {})) if (Object.prototype.hasOwnProperty.call(it.properties, k)) p[k] = it.properties[k];
      p._check_id = tok;
      var d = {};
      for (k in it) if (Object.prototype.hasOwnProperty.call(it, k)) d[k] = it[k];
      d.properties = p;
      out.push(d);
      out.push({ id: c.variantId, quantity: 1, properties: { _check_for: tok } });
    });
    return out;
  }
  function acDrop(lines) {
    var toks = {};
    (Array.isArray(lines) ? lines : [lines]).forEach(function (it) {
      var v = it && it.properties && it.properties._check_id;
      if (v) toks[v] = 1;
    });
    if (!Object.keys(toks).length) return Promise.resolve();
    var R = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(R + "cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }).then(function (cart) {
      var keys = (cart.items || []).filter(function (li) { return li.properties && toks[li.properties._check_for]; }).map(function (li) { return li.key; });
      return keys.reduce(function (pr, key) {
        return pr.then(function () {
          return fetch(R + "cart/change.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: key, quantity: 0 }) }).catch(function () {});
        });
      }, Promise.resolve());
    }).catch(function () {});
  }
  var AC_PEN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  /* v2 (fix pass 1 Nov 2026, review HIGH 3): "£5 per cart item", so an add that makes n design lines
     (Magnet Studio: one line per design; gang: one per sheet) charges n checks and the tile says the
     total it will charge: n x the variant's price, in the visitor's currency, formatted from the theme's
     money sample like money_without_trailing_zeros. The Studio passes o.checkLines (number or function). */
  var acCount = 1;
  function acN() {
    var n = typeof acCount === "function" ? acCount() : acCount;
    n = Math.floor(Number(n) || 1);
    return n > 1 ? n : 1;
  }
  function acMoney(c, cents) {
    var m = /1(\D?)234(?:(\D)56)?/.exec(String(c.sample || "1,234.56")), th = m ? m[1] : ",", dec = m && m[2] ? m[2] : ".";
    var w = Math.floor(cents / 100), f = cents % 100;
    var out = String(w).replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? dec + (f < 10 ? "0" : "") + f : "");
    var p = String(c.price || ""), r = /\d(?:[\d.,'\s  ]*\d)?/.exec(p);
    return r ? p.slice(0, r.index) + out + p.slice(r.index + r[0].length) : out;
  }
  function acFee(c) {
    var n = acN();
    if (n === 1 || !c.t.feeTpl || !(Number(c.priceCents) > 0)) return c.t.fee || "";
    return String(c.t.feeTpl).replace("[price]", acMoney(c, n * Number(c.priceCents)));
  }
  function acHTML() {
    var c = acCfg();
    if (!c) return "";
    return '<label class="lpc__chk" data-lpc-chk>' +
      '<span class="lpc__chki">' + AC_PEN + "</span>" +
      '<span class="lpc__chkt"><span class="lpc__chkk">' + esc(c.t.key || "") + '</span><span class="lpc__chkv">' + esc(c.t.value || "") + "</span></span>" +
      '<span class="lpc__chkb">' + esc(acFee(c)) + "</span>" +
      '<input type="checkbox" class="lpc__chkin" data-lpc-chkin' + (acOn ? " checked" : "") + ">" +
      '<span class="lpc__chkbox" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 12.5 5 5 10-11"/></svg></span>' +
      "</label>";
  }
  function stkFmt(s, vars){ s=String(s==null?"":s); return vars ? s.replace(/\[(\w+)\]/g, function(m, n){ return vars[n]!=null ? String(vars[n]) : m; }) : s; }
  function fmt(s, vars) {
    s = String(s == null ? "" : s);
    return vars ? s.replace(/\[(\w+)\]/g, function (m, n) { return vars[n] != null ? String(vars[n]) : m; }) : s;
  }
  "use strict";
  if (window.LabProofConfirm) return;

  /* SAPIENS, ONE WEIGHT, and the lab's own card vocabulary (Thomas, 12 Sep
     18:05: the overlay's title and labels are Sapiens; the details are tiles,
     not a table). The Add to cart button is still the product page's own node,
     cloned, and nothing here touches it. */
  var FAM = "'Sapiens-Regular','Sapiens',sans-serif";
  /* ONE GAP (Thomas, 12 Sep 23:10: "spacing here is shit"). Every block in the
     window is this far from the next one, and the foot's bottom padding is the
     same figure as the head's top padding, so the last element sits the same
     distance from the bottom edge as the title does from the top. */
  var GAP = "14px";
  /* ONE TOKEN PAIR, light and dark, so the modal follows the lab's theme
     instead of staying white inside it (Thomas, 12 Sep 22:35). The dark values
     are the lab's own .app.dark tokens; the dotted ground is the editor
     canvas's own pattern in both. */
  var CSS = [
    ".lpc{--lpc-bg:#ffffff;--lpc-panel:#fbfbfc;--lpc-line:#e9e9ee;--lpc-ink:#1b1b1b;--lpc-muted:#6a6a74;",
    "--lpc-flat:#eff0f3;--lpc-flat-h:#e5e6ea;--lpc-dot:#ececf1;--lpc-ground:#ffffff;--lpc-tile:#ececef;",
    "--lpc-dash:#cfcfda;--lpc-gap:" + GAP + ";",
    /* TWO LABEL TOKENS THAT PASS 4.5:1 IN BOTH MODES (measured, 12 Sep).
       --lpc-danger is the REMOVE word and the error line: #e0484f read 3.54:1 on
       the flat tile in light and 3.35:1 in dark, so each mode gets its own red -
       #b32d26 (the x button's own hover red) on light = 5.55:1, #f0787e on the
       dark tile = 4.94:1.
       --lpc-onflat is any muted label sitting ON a flat tile or a white badge:
       #7a7a85 read 3.72:1 on #eff0f3, so light darkens to #6a6a74 = 4.69:1 on the
       tile and 5.35:1 on the badge. Dark already passed (4.83 / 5.61), so it
       keeps --lpc-muted's value. */
    "--lpc-danger:#b32d26;--lpc-onflat:#6a6a74;",
    "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:18px}",
    '.lpc[data-theme="dark"]{--lpc-bg:#232327;--lpc-panel:#1b1b1f;--lpc-line:#3a3a41;--lpc-ink:#f1f1f2;',
    /* v2.6 DARK (item 9): every control background clears 3:1 against its
       surface. #6f6f78 is the least light grey that does so on both the panel
       #1b1b1f (3.27:1) and the window #232327 (3.04:1); white ink on it is
       5.2:1, so --lpc-onflat is white in dark. The dashed add tile takes a
       #7c7c86 boundary (3.98:1). Hairlines keep --lpc-line. */
    "--lpc-muted:#9a9aa2;--lpc-flat:#6f6f78;--lpc-flat-h:#7a7a83;--lpc-dot:#35353c;--lpc-ground:#232327;--lpc-tile:#6f6f78;",
    "--lpc-dash:#7c7c86;--lpc-danger:#f0787e;--lpc-onflat:#ffffff}",
    '.lpc[data-theme="dark"] .lpc__lead,.lpc[data-theme="dark"] .lpc__confirm,.lpc[data-theme="dark"] .lpc__tk{color:#b4b4bc}',
    ".lpc[hidden]{display:none}",
    /* ONE EVEN VEIL over the lab while the window is open (item 9) */
    ".lpc__scrim{position:absolute;inset:0;border:0;padding:0;background:rgba(12,12,14,.6);cursor:pointer}",
    /* NO BROWSER FOCUS RING (item 9): outline none on focus, the brand yellow on :focus-visible */
    ".lpc *:focus{outline:none}",
    ".lpc button:focus-visible,.lpc a:focus-visible,.lpc input:focus-visible,.lpc [tabindex]:focus-visible{outline:2px solid #ffc63c;outline-offset:2px;box-shadow:none}",
    ".lpc .lpc__act--rm:focus-visible{box-shadow:inset 0 0 0 1.5px #f7d6d6}",
    /* v2.5: the window is sized to the VISUAL viewport, not to 100vh. 100vh is
       the layout viewport, which on a phone includes the browser chrome that is
       covering the panel. --lpc-vh is re-measured on every resize. */
    ".lpc__panel{position:relative;width:min(760px,100%);max-height:calc(var(--lpc-vh,100vh) - 36px);display:flex;flex-direction:column;",
    "background:var(--lpc-bg);color:var(--lpc-ink);border:1px solid var(--lpc-line);border-radius:18px;overflow:hidden}",
    ".lpc__head{display:flex;align-items:flex-start;gap:12px;padding:var(--lpc-gap) var(--lpc-gap) 0}",
    /* the same 38px tinted tile the Artwork / Design / Order cards carry */
    ".lpc__hic{flex:none;width:38px;height:38px;border-radius:11px;background:var(--lpc-tile);color:var(--lpc-ink);display:flex;align-items:center;justify-content:center}",
    ".lpc__hic svg{width:19px;height:19px}",
    ".lpc__ht{flex:1;min-width:0}",
    ".lpc__t{margin:0;font-family:" + FAM + ";font-weight:400;font-size:19px;line-height:1.25;letter-spacing:.04em;color:var(--lpc-ink)}",
    ".lpc__lead{margin:5px 0 0;font-size:13.5px;line-height:1.45;color:var(--lpc-onflat)}",
    ".lpc__x{flex:none;width:38px;height:38px;display:flex;align-items:center;justify-content:center;border:1.5px solid #f7d6d6;border-radius:12px;background:#fdf1f1;color:#e0484f;cursor:pointer;transition:background .15s,border-color .15s,color .15s}",
    ".lpc__x:hover{background:#fbe3e3;border-color:#efb9b9;color:#b32d26}",
    ".lpc__x svg{width:17px;height:17px}",
    /* round 2 (owner 31 Oct): the Studio bar's moon and cm/in switch in the head, the bar's own sizes and colours */
    ".lpc__bar{flex:none;display:flex;align-items:center;gap:8px}",
    ".lpc__units{position:relative;flex:none;width:auto;min-width:74px;height:38px;margin:0;padding:3px;display:grid;grid-template-columns:1fr 1fr;align-items:center;border:1px solid var(--lpc-line);border-radius:12px;background:var(--lpc-bg);color:var(--lpc-ink);cursor:pointer;-webkit-appearance:none;appearance:none}",
    ".lpc__uk{position:absolute;left:3px;top:3px;bottom:3px;width:calc(50% - 3px);border-radius:9px;background:#147f49;transition:transform .16s ease}",
    ".lpc__units.is-in .lpc__uk{transform:translateX(100%)}",
    ".lpc__uo{position:relative;z-index:1;text-align:center;color:#74747f;font-family:" + FAM + ";font-size:11px;font-weight:400;letter-spacing:.08em;text-transform:uppercase;line-height:1;padding:0 5px;white-space:nowrap}",
    ".lpc__units:not(.is-in) .lpc__uo:nth-of-type(2),.lpc__units.is-in .lpc__uo:nth-of-type(3){color:#fff}",
    ".lpc__th{flex:none;width:38px;height:38px;padding:0;display:flex;align-items:center;justify-content:center;border:1.5px solid var(--lpc-line);border-radius:12px;background:var(--lpc-bg);color:var(--lpc-ink);cursor:pointer;-webkit-appearance:none;appearance:none}",
    ".lpc__th svg{width:17px;height:17px}",
    "@media (hover:hover){.lpc__units:hover,.lpc__th:hover{border-color:#9a9aa2}.lpc__units:hover .lpc__uk{background:#116c3e}}",
    ".lpc__units:focus-visible,.lpc__th:focus-visible{outline:2px solid var(--lpc-ink);outline-offset:2px}",
    '.lpc[data-theme="dark"] .lpc__units,.lpc[data-theme="dark"] .lpc__th{background:#2e2e34;border-color:#7c7c86}',
    '.lpc[data-theme="dark"] .lpc__uo{color:#9a9aa2}',
    '.lpc[data-theme="dark"] .lpc__units:not(.is-in) .lpc__uo:nth-of-type(2),.lpc[data-theme="dark"] .lpc__units.is-in .lpc__uo:nth-of-type(3){color:#fff}',
    '.lpc[data-theme="dark"] .lpc__th{color:#ffc63c}',
    "@media (max-width:600px){.lpc__bar{gap:6px}.lpc__units{width:auto;min-width:52px;border-radius:11px}.lpc__uk{border-radius:8px}.lpc__uo{font-size:10px;letter-spacing:0;padding:0 1px}.lpc__th{border-radius:11px}}",
    ".lpc__body{flex:1;min-height:0;overflow:auto;padding:var(--lpc-gap) var(--lpc-gap) 0}",
    /* FOUR QUARTERS, ALWAYS (Thomas, 12 Sep 23:05: "add designs should appear
       in the 4 quarter tiles centred"). Two columns, and grid-auto-rows:1fr
       makes every row the height of the tallest, so all four quarters are the
       same height at any width. */
    ".lpc__proofs{position:relative;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-auto-rows:1fr;gap:var(--lpc-gap);align-items:stretch}",
    ".lpc__cell{position:relative;display:flex;flex-direction:column;height:100%;min-height:0;border:1px solid var(--lpc-line);border-radius:14px;overflow:hidden;background:var(--lpc-panel)}",
    /* THE GREY DOTTED GROUND (Thomas, 12 Sep 22:30: "white on white looks a bit
       naff cos you can barely see the cut line"). The editor canvas's own
       pattern, so a white sticker and its cut line read against it. */
    /* THE CELLS SHRINK BEFORE THE WINDOW SCROLLS (v2.5). At 1440x950 the 4:3
       image box pushed .lpc__body to 818px inside 632px, so the bottom row's
       steppers, the details tiles, the tick and ADD TO CART were all below the
       fold. Every proof box is now ONE height, --lpc-imgh, set by fit() from
       the height that is actually left after the head, the tiles and the foot.
       Equal heights whether the grid holds four proofs or three and an add
       tile, which is what the 4:3 ratio was buying. */
    ".lpc__proofimg{position:relative;display:block;width:100%;flex:none;height:var(--lpc-imgh,180px);",
    "background:radial-gradient(circle at 50% 50%, var(--lpc-dot) 1.2px, transparent 1.7px) 0 0/26px 26px, var(--lpc-ground);",
    "border:0;padding:0;cursor:zoom-in}",
    ".lpc__proofimg img{display:block;width:100%;height:100%;object-fit:contain;background:transparent}",
    ".lpc__cap{flex:none;height:30px;display:flex;align-items:center;justify-content:center;padding:0 10px;",
    "font-family:" + FAM + ";font-weight:400;font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--lpc-onflat);",
    "border-top:1px solid var(--lpc-line);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}",
    ".lpc__capt{display:block;min-width:0;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}",
    /* EVERY TILE'S CONTROLS ON ONE LINE (v2.6, item 4, Thomas: "the buttons
       should all be on a single line, view can be an eye icon, remove just a
       red x etc.."). Eye, pencil, red x, then minus / number / plus, all 34px
       like the drawer's x, centred. Where a cell is too narrow for the 252px
       row (a 1280-wide window's one-row fallback, a phone) the row wraps to
       two centred lines and the 8px padding keeps every control off the cell
       edge (item 13). */
    ".lpc__ctl{flex:none;display:flex;flex-wrap:nowrap;align-items:center;justify-content:center;gap:6px;padding:8px 8px}",
    /* v2.8: a short window (1280x720) drops the lead and tightens the gaps before it would scroll */
    ".lpc--compact{--lpc-gap:10px}.lpc--compact .lpc__lead{display:none}.lpc--compact .lpc__cap{height:26px}.lpc--compact .lpc__ctl{padding:6px 8px}.lpc--compact .lpc__confirm{margin-bottom:8px}",
    ".lpc__act{flex:none;width:34px;height:34px;padding:0;border:0;border-radius:10px;background:var(--lpc-flat);color:var(--lpc-ink);",
    "display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .12s,color .12s}",
    ".lpc__act svg{width:17px;height:17px}",
    ".lpc__act:hover{background:var(--lpc-flat-h)}",
    /* the drawer's own remove control: 34x34, 10px radius, #f7d6d6 hairline on
       #fdf1f1, #e0484f x; dark is #a24f55 with a white x (3.06:1 on the window) */
    ".lpc__act--rm{background:#fdf1f1;color:#e0484f;box-shadow:inset 0 0 0 1.5px #f7d6d6}",
    ".lpc__act--rm:hover{background:#fbe3e3;color:#b32d26;box-shadow:inset 0 0 0 1.5px #efb9b9}",
    '.lpc[data-theme="dark"] .lpc__act--rm{background:#aa5359;color:#fff;box-shadow:none}',
    /* #f1f1f2 on #6f6f78 measured 4.41:1; white is 5.2:1 */
    '.lpc[data-theme="dark"] .lpc__act,.lpc[data-theme="dark"] .lpc__q button,.lpc[data-theme="dark"] .lpc__q input{color:#ffffff}',
    '.lpc[data-theme="dark"] .lpc__act--rm:hover{background:#b35c62;color:#fff}',
    ".lpc__act[disabled]{opacity:.4;cursor:default}",
    /* v2.9.2 (R8): the edit tile in yellow, the remove tile's own family. Light: #9c6605 on #fdf2d7 4.35:1,
       the #c07d00 ring 3.4:1 on white. Dark: #ffc63c on #2b2416 9.8:1, the 2px #ffc63c outline. */
    ".lpc__act--edit{background:#fdf2d7;color:#9c6605;box-shadow:inset 0 0 0 1.5px #c07d00}",
    ".lpc__act--edit:hover{background:#fbe7b3;color:#7a4f04;box-shadow:inset 0 0 0 1.5px #9c6605}",
    '.lpc[data-theme="dark"] .lpc__act--edit{background:#2b2416;color:#ffc63c;box-shadow:inset 0 0 0 2px #ffc63c}',
    '.lpc[data-theme="dark"] .lpc__act--edit:hover{background:#3a2f18;color:#ffd35f;box-shadow:inset 0 0 0 2px #ffd35f}',
    /* the same stepper as the ORDER quarter, at the same 34px as the icons */
    ".lpc__q{display:contents}",
    ".lpc__q button{width:34px;height:34px;flex:none;border-radius:50%;border:0;background:var(--lpc-flat);color:var(--lpc-ink);",
    "font-size:17px;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0}",
    ".lpc__q button:hover{background:var(--lpc-flat-h)}",
    ".lpc__q input{width:52px;height:34px;text-align:center;border:0;border-radius:10px;padding:0 2px;",
    "background:var(--lpc-flat);color:var(--lpc-ink);font-family:" + FAM + ";font-weight:400;font-size:13px;-moz-appearance:textfield}",
    ".lpc__q input::-webkit-outer-spin-button,.lpc__q input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}",
    /* AN EMPTY QUARTER IS THE ADD TILE, centred. v2.6 (item 3, Thomas: "these
       numbers and add a design should be green circles and buttons larger and
       centred"): the number is a 30px green circle, the add control a 48px
       green circle with a white upload glyph, both centred where they sit. */
    ".lpc__cell--add{border:1.5px dashed var(--lpc-dash);background:var(--lpc-panel);color:var(--lpc-onflat);",
    "align-items:center;justify-content:center;gap:8px;cursor:pointer;padding:10px}",
    /* v2.9.2 (R7): hover is a visible change - green boundary, light green fill, pointer */
    ".lpc__cell--add:hover{border-color:#1fab63;background:#eef8f1;color:var(--lpc-ink);cursor:pointer}",
    '.lpc[data-theme="dark"] .lpc__cell--add:hover{border-color:#1fab63;background:#1e2f26;color:#ffffff}',
    ".lpc__slotn{position:absolute;z-index:1;top:8px;left:8px;width:30px;height:30px;border-radius:50%;background:#1fab63;color:#ffffff;",
    "display:flex;align-items:center;justify-content:center;font-family:" + FAM + ";font-weight:400;font-size:13px}",
    ".lpc__addic{width:48px;height:48px;border-radius:50%;background:#1fab63;color:#ffffff;display:flex;align-items:center;justify-content:center}",
    ".lpc__addic svg{width:22px;height:22px}",
    ".lpc__addt{font-family:" + FAM + ";font-weight:400;font-size:11px;letter-spacing:.1em;text-transform:uppercase;text-align:center}",
    ".lpc__big{position:fixed;inset:0;z-index:4;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(16,16,18,.72);cursor:zoom-out}",
    ".lpc__big img{max-width:96%;max-height:92%;object-fit:contain;border-radius:14px;",
    "background:radial-gradient(circle at 50% 50%, var(--lpc-dot) 1.2px, transparent 1.7px) 0 0/26px 26px, var(--lpc-ground)}",
    /* ONE TIGHT STRIP, not padded tiles (v2.6, item 2, Thomas: "this icon strip
       looks spaced out and awful."). One hairline row, a small icon + label +
       value per item, even gaps, values in the ink at 13px. Below 600px each
       item stacks icon-and-label over value so five still fit one row. */
    /* v2.9.3 (Thomas, 17 Sep: the cell read "6.4 X..."). NO CELL MAY CLIP ITS VALUE. The
       strip wraps to a second line before any value is cut: each cell asks for the width
       its own text needs (flex-basis auto, no shrink below content) and the value no
       longer ellipsises. Measured at 1440x950 and 390x844, scrollWidth == clientWidth on
       every cell. */
    ".lpc__tiles{display:flex;flex-wrap:wrap;align-items:stretch;justify-content:space-between;margin:var(--lpc-gap) 0 0;border-top:1px solid var(--lpc-line);border-bottom:1px solid var(--lpc-line)}",
    ".lpc__tile{flex:1 1 auto;min-width:0;display:flex;align-items:center;justify-content:center;gap:7px;padding:10px 6px;border-left:1px solid var(--lpc-line);background:transparent}",
    ".lpc__tile:first-child{border-left:0}",
    ".lpc__ti{flex:none;width:16px;height:16px;color:var(--lpc-muted);display:flex;align-items:center;justify-content:center}",
    ".lpc__ti svg{width:15px;height:15px}",
    ".lpc__tk{font-family:" + FAM + ";font-weight:400;font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--lpc-onflat);white-space:nowrap}",
    ".lpc__tv{min-width:0;font-family:" + FAM + ";font-weight:400;font-size:13px;letter-spacing:.03em;color:var(--lpc-ink);overflow:visible;text-overflow:clip;white-space:nowrap}",
    "@media (max-width:600px){.lpc__tile{flex-wrap:wrap;gap:3px 5px;padding:7px 3px}.lpc__tk{font-size:8.5px}.lpc__tv{flex-basis:100%;text-align:center;font-size:12px}}",
    /* P240 artwork check tile (mocks 1 and 5, approved 1 Nov): full width under the details,
       orange tint, 1.5px #f5841f edge, white circle with the orange pencil, key in #f5841f,
       value in ink (wraps on a phone), "+ fee" badge 4px, orange box unticked by default */
    ".lpc .lpc__chk{grid-column:1/-1;flex:1 1 100%;position:relative;box-sizing:border-box;display:grid;grid-template-columns:32px minmax(0,1fr) auto 22px;align-items:center;column-gap:10px;min-height:60px;margin:0;padding:10px 12px;border:1.5px solid #f5841f;border-radius:12px;background:#fff3e8;cursor:pointer;text-align:left;-webkit-tap-highlight-color:transparent}",
    ".lpc .lpc__chki{width:32px;height:32px;border-radius:50%;background:#ffffff;color:#f5841f;display:flex;align-items:center;justify-content:center}",
    ".lpc .lpc__chki svg{width:16px;height:16px}",
    ".lpc .lpc__chkt{display:flex;flex-direction:column;gap:3px;min-width:0}",
    ".lpc .lpc__chkk{font-family:" + FAM + ";font-weight:400;font-size:12px;letter-spacing:.5px;line-height:1.15;text-transform:uppercase;color:#f5841f}",
    ".lpc .lpc__chkv{font-family:" + FAM + ";font-weight:400;font-size:15px;letter-spacing:.5px;line-height:1.15;text-transform:uppercase;color:#1a1a20;white-space:normal;overflow-wrap:anywhere}",
    ".lpc .lpc__chkb{font-family:" + FAM + ";font-weight:400;font-size:12px;letter-spacing:.5px;line-height:1;padding:5px 7px;border-radius:4px;background:#b4570f;color:#ffe0c2;white-space:nowrap}",
    ".lpc .lpc__chkin{position:absolute;opacity:0;width:1px;height:1px;margin:0;pointer-events:none}",
    ".lpc .lpc__chkbox{box-sizing:border-box;width:22px;height:22px;border:1.5px solid #f5841f;border-radius:7px;background:#ffffff;display:flex;align-items:center;justify-content:center;color:transparent;transition:background .12s,color .12s}",
    ".lpc .lpc__chkbox svg{width:13px;height:13px}",
    ".lpc .lpc__chkin:checked + .lpc__chkbox{background:#f5841f;color:#ffffff}",
    ".lpc .lpc__chkin:focus-visible + .lpc__chkbox{outline:2px solid #f5841f;outline-offset:2px}",
    ".lpc .lpc__chkin:disabled + .lpc__chkbox{opacity:.6}",
    "@media (hover:hover){.lpc .lpc__chk:hover{background:#ffead6}.lpc[data-theme=\"dark\"] .lpc__chk:hover{background:#271f19}}",
    '.lpc[data-theme="dark"] .lpc__chk{background:#2e261f}',
    '.lpc[data-theme="dark"] .lpc__chkv{color:#ffffff}',
    '.lpc[data-theme="dark"] .lpc__chkb{background:#382b20;color:#f4b683}',
    '.lpc[data-theme="dark"] .lpc__chkbox{background:transparent}',
    '.lpc[data-theme="dark"] .lpc__chkin:checked + .lpc__chkbox{background:#f5841f;color:#ffffff}',
    "@media (max-width:600px){.lpc .lpc__chkv{font-size:14px}}",
    ".lpc__foot{flex:none;padding:var(--lpc-gap);border-top:1px solid var(--lpc-line);background:var(--lpc-bg)}",
    ".lpc__confirm{display:flex;gap:11px;align-items:center;justify-content:center;text-align:center;font-family:" + FAM + ";font-weight:400;font-size:12px;letter-spacing:.06em;text-transform:uppercase;line-height:1.45;color:var(--lpc-onflat);cursor:pointer;margin-bottom:var(--lpc-gap)}",
    ".lpc__confirm input{position:absolute;opacity:0;width:1px;height:1px;pointer-events:none}",
    ".lpc__box{flex:none;width:22px;height:22px;border:1.5px solid var(--lpc-line);border-radius:7px;background:var(--lpc-bg);display:flex;align-items:center;justify-content:center;color:transparent;transition:background .12s,border-color .12s,color .12s}",
    ".lpc__box svg{width:13px;height:13px}",
    ".lpc__confirm input:checked + .lpc__box{background:#1b1b1b;border-color:#1b1b1b;color:#fff}",
    '.lpc[data-theme="dark"] .lpc__confirm input:checked + .lpc__box{background:#f1f1f2;border-color:#f1f1f2;color:#1a1a20}',
    ".lpc__confirm input:focus-visible + .lpc__box{outline:2px solid var(--lpc-ink);outline-offset:2px}",
    ".lpc__add[disabled]{opacity:.5;cursor:default}",
    /* THE LIVE PAGE'S DOWNLOAD GROUP (Thomas, 12 Sep 23:00: "the download files
       button looks NOTHING like my download buttons on the lab pages"). These
       are the computed values of button.dl.dl--zip on
       stickiply.com/pages/proof-lab, read off the rendered element and off the
       last generation of the .dl rules in assets/proof-lab.css. The overlay is
       in the LIGHT DOM, where that sheet cannot reach, so they are restated
       here as literals - not approximated. */
    /* v2.6 (item 1, Thomas: "download files left and add to cart right."):
       ONE row under the rights tick - the green download tile left, the
       product page's own Add to cart right, equal heights, one gap. A lab
       that passes no onDownload keeps the full-width button. */
    ".lpc__row{display:flex;align-items:stretch;gap:var(--lpc-gap)}",
    ".lpc__row>*{flex:1 1 0;min-width:0}",
    ".lpc__row .lpc__acts{margin-top:0;display:flex}",
    ".lpc__row .lpc__btn{height:100%;min-height:56px;padding:9px 12px}",
    ".lpc__row [data-lpc-slot]{display:flex}",
    ".lpc__row .lpc__add{height:auto;min-height:56px;width:100%;margin:0}",
    /* Thomas, 1 Oct: "increase add to cart text" - larger than the download tile's 14px title */
    ".lpc .lpc__add,.lpc .lpc__add span{font-size:18px !important;letter-spacing:.08em}",
    "@media (max-width:600px){.lpc .lpc__add,.lpc .lpc__add span{font-size:15px !important}}",
    /* dark: the primary is the light tile with dark ink, like the selected
       picker row - never black on black (item 9) */
    '.lpc[data-theme="dark"] .lpc__add{background:#f1f1f2 !important;color:#1a1a20 !important;border-color:#f1f1f2 !important}',
    '.lpc[data-theme="dark"] .lpc__add:hover{background:#ffffff !important}',
    /* v2.8: at 174px the 14px label wrapped to two lines (measured 390x844); 12.5px on one line with a 32px icon */
    "@media (max-width:600px){.lpc__row .lpc__btn small{display:none}.lpc__row .lpc__btn{padding:9px 8px;gap:8px;min-height:52px;font-size:12.5px;letter-spacing:.03em;white-space:normal;container-type:inline-size}.lpc__row .lpc__btx{text-wrap:balance}@container (max-width:150px){.lpc__row .lpc__btx{letter-spacing:0}}@container (max-width:130px){.lpc__row .lpc__btx{font-size:11px}}.lpc__row .lpc__bic{width:32px;height:32px}.lpc__row .lpc__add{min-height:52px}}",
    ".lpc__acts{margin-top:var(--lpc-gap)}",
    ".lpc__btn{display:flex;align-items:center;gap:11px;width:100%;text-align:left;",
    "border:none;border-radius:14px;padding:16px 14px;background:#147f49;color:#ffffff;box-shadow:none;",
    "font-family:" + FAM + ";font-weight:400;font-size:14px;letter-spacing:.05em;cursor:pointer}",
    ".lpc__btn:not(:disabled):hover{background:#116c3e;box-shadow:none}",
    ".lpc__btn:not(:disabled):active{background:#0f5f37;box-shadow:none}",
    ".lpc__btn:disabled{opacity:.25;cursor:default;box-shadow:none}",
    ".lpc__btx{flex:1;min-width:0}",
    ".lpc__btn small{display:block;font-family:inherit;font-weight:400;letter-spacing:0;font-size:11.5px;margin-top:2px;color:#ffffff}",
    ".lpc__bic{flex:none;width:38px;height:38px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.25);color:#fff}",
    ".lpc__bic svg{width:19px;height:19px}",
    /* an empty status line must not hold the window open - the last element has
       to sit one gap from the bottom edge */
    ".lpc__status{margin-top:var(--lpc-gap);min-height:17px;font-family:" + FAM + ";font-weight:400;font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:var(--lpc-muted);text-align:center}",
    ".lpc__status.is-fading{opacity:0;transition:opacity .4s}",
    ".lpc__status:empty{display:none}",
    ".lpc__status.is-error{color:var(--lpc-danger)}",
    ".lpc__status.is-done{color:#137a46}",
    '.lpc[data-theme="dark"] .lpc__status.is-done{color:#1fab63}',
    /* THE UPLOAD IS A BAR, NOT A SENTENCE (Thomas, 12 Sep 22:10). The track is
       the panel scrollbar's - 10px, 99px radius - and the fill is the lab's
       green. Driven by the BYTES on the wire, summed across every file. */
    ".lpc__prog{display:none;margin-top:var(--lpc-gap)}",
    ".lpc__prog.is-on{display:block}",
    ".lpc__progtrack{height:10px;border-radius:99px;background:var(--lpc-flat);overflow:hidden}",
    ".lpc__progfill{height:100%;width:0%;border-radius:99px;background:#1fab63;transition:width .18s linear}",
    ".lpc__prog.is-done .lpc__progfill{width:100%}",
    ".lpc__progcap{margin-top:7px;font-family:" + FAM + ";font-weight:400;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--lpc-muted);text-align:center}",
    ".lpc__progcap.is-done{color:#137a46}",
    '.lpc[data-theme="dark"] .lpc__progcap.is-done{color:#1fab63}',
    /* THE SUCCESS STATE REPLACES THE WINDOW (Thomas, 12 Sep 23:10: "why are we
       showing these options again... or the title and subtitle text etc... just
       view cart or close, or add more designs"). One gap between every block,
       top and bottom the same. */
    /* flex:1 so that on a phone, where the panel is the whole screen, the
       success block sits in the middle of it rather than at the top */
    ".lpc__done{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;",
    "gap:var(--lpc-gap);padding:calc(var(--lpc-gap) * 2) var(--lpc-gap)}",
    ".lpc__donetick{width:46px;height:46px;border-radius:50%;background:#eef8f1;color:#1fab63;display:flex;align-items:center;justify-content:center}",
    ".lpc__donetick svg{width:24px;height:24px}",
    ".lpc__donet{font-family:" + FAM + ";font-weight:400;font-size:16px;letter-spacing:.06em;text-transform:uppercase;color:var(--lpc-ink);margin:0}",
    ".lpc__dones{font-family:" + FAM + ";font-weight:400;font-size:12px;letter-spacing:.05em;color:var(--lpc-muted);margin:0}",
    ".lpc__doneacts{display:flex;flex-direction:column;gap:var(--lpc-gap);width:min(300px,100%)}",
    /* v2.9.2 (R10): the order summary on the success card - thumbnails on the dotted ground, the
       option strip at full width, the total and the per-piece price. The card is always light:
       the done class re-pins the light tokens whatever the lab's mode. */
    ".lpc--done{--lpc-bg:#ffffff;--lpc-panel:#fbfbfc;--lpc-line:#e9e9ee;--lpc-ink:#1b1b1b;--lpc-muted:#6a6a74;--lpc-flat:#eff0f3;--lpc-flat-h:#e5e6ea;--lpc-dot:#ececf1;--lpc-ground:#ffffff;--lpc-tile:#ececef;--lpc-dash:#cfcfda;--lpc-danger:#b32d26;--lpc-onflat:#6a6a74}",
    ".lpc--done .lpc__panel{background:#ffffff;color:#1b1b1b;border-color:#e9e9ee}",
    ".lpc--done .lpc__done{padding:var(--lpc-gap);gap:12px;overflow:auto;min-height:0}",
    ".lpc__donethumbs{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}",
    ".lpc__donethumb{width:72px;height:72px;border-radius:12px;border:1px solid #e9e9ee;padding:5px;object-fit:contain;",
    "background:radial-gradient(circle at 50% 50%, #ececf1 1.2px, transparent 1.7px) 0 0/22px 22px, #ffffff}",
    ".lpc--done .lpc__tiles{width:100%;margin:0;border-color:#e9e9ee}",
    ".lpc--done .lpc__tile{border-color:#e9e9ee}.lpc--done .lpc__tk{color:#6a6a74}.lpc--done .lpc__tv{color:#1b1b1b}.lpc--done .lpc__ti{color:#6a6a74}",
    ".lpc__donetotal{display:flex;align-items:baseline;justify-content:center;gap:10px;font-family:" + FAM + ";font-weight:400}",
    ".lpc__donetotal b{font-weight:400;font-size:22px;letter-spacing:.02em;color:#1b1b1b;font-variant-numeric:tabular-nums}",
    ".lpc__donetotal span{font-size:11.5px;letter-spacing:.06em;text-transform:uppercase;color:#137a46}",
    ".lpc--done .skcb--primary{background:#1a1a20;color:#ffc63c}.lpc--done .skcb--primary:hover{background:#24242c}",
    ".lpc--done .skcb--secondary{background:#eff0f3;color:#1b1b1b}.lpc--done .skcb--secondary::before{border-color:#e9e9ee}",
    ".lpc--done .lpc__donetick{background:#eef8f1;color:#1fab63}.lpc--done .lpc__donet{color:#1b1b1b}.lpc--done .lpc__dones{color:#6a6a74}",
    /* the cart drawer's own buttons, measured on the live product page:
       .skc-btn min-height 48, padding 12px 18px, radius 12px, border 0,
       Sapiens 13px / .05em uppercase; primary #1a1a20 on #ffc63c; secondary
       #eff0f3 with a 1.5px hairline; :active scale .98; icon 17px, gap 8. */
    ".skcb{position:relative;isolation:isolate;display:flex;align-items:center;justify-content:center;width:100%;min-height:48px;padding:12px 18px;",
    "border-radius:12px;border:0;box-shadow:none;font-family:" + FAM + ";font-weight:400;font-size:13px;letter-spacing:.05em;text-transform:uppercase;",
    "text-decoration:none;cursor:pointer}",
    ".skcb:active{scale:.98}",
    ".skcb--primary{background:#1a1a20;color:#ffc63c}",
    ".skcb--primary:hover{background:#24242c}",
    '.lpc[data-theme="dark"] .skcb--primary{background:#f1f1f2;color:#1a1a20}',
    /* 17 Sep: #f1f1f2 on the #6f6f78 tile measured 4.41:1; white is 4.98:1 */
    '.lpc[data-theme="dark"] .skcb--secondary{color:#ffffff}',
    '.lpc[data-theme="dark"] .skcb--primary:hover{background:#ffffff}',
    ".skcb--secondary{background:var(--lpc-flat);color:var(--lpc-ink)}",
    ".skcb--secondary::before{content:'';position:absolute;inset:0;border:1.5px solid var(--lpc-line);border-radius:inherit;pointer-events:none}",
    ".skcb .skc-i{width:17px;height:17px;margin-right:8px}",
    "@media (max-width:600px){.lpc{padding:0}.lpc__panel{width:100%;max-height:var(--lpc-vh,100vh);height:100%;border:0;border-radius:0}",
    ".lpc__q input{width:44px}}",
    /* one design per order (Custom Magnets, v2.6): a single proof at the
       window's full width, not half of it */
    ".lpc__proofs--one{grid-template-columns:minmax(0,1fr)}"
  ].join("");

  function css() {
    var s = document.getElementById("lpc-css");
    if (!s) {
      s = document.createElement("style");
      s.id = "lpc-css";
      document.head.appendChild(s);
    }
    /* rewritten every open: a stale sheet from an earlier version of this file
       cached in the page would otherwise style the new markup */
    if (s.textContent !== CSS) s.textContent = CSS;
  }

  /* one glyph per fact, so the tiles read at a glance */
  var ICONS = {
    /* builder audit row 29: bleed = the cut line inside the printed edge; colour = a swatch */
    bleed:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><rect x="7.5" y="7.5" width="9" height="9" rx="1.5" stroke-dasharray="2.5 2.5"/></svg>',
    colour:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a9 9 0 1 0 0 18c1.4 0 2-1 2-2 0-1.4-1.2-1.6-1.2-2.8 0-1 .8-1.7 1.8-1.7H17a4 4 0 0 0 4-4c0-4-4-7.5-9-7.5Z"/><circle cx="7.5" cy="11" r="1"/><circle cx="10.5" cy="7.5" r="1"/><circle cx="15" cy="7.5" r="1"/></svg>',
    size:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21 21 3"/><path d="M3 15v6h6"/><path d="M21 9V3h-6"/></svg>',
    finish:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 8v8l8 5 8-5V8Z"/><path d="m4 8 8 5 8-5"/><path d="M12 13v8"/></svg>',
    quantity:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
    designs:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg>',
    price:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 6.5a4.5 4.5 0 0 0-8 2.8V14"/><path d="M7 14h8"/><path d="M7 19h11"/><path d="M9 14v5"/></svg>',
    shape:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M12 3c2.5 0 3 2.2 5 2.6 2 .5 4 1.6 3.4 3.9-.5 2.2 1 3-.4 5-1.3 1.9-1.4 4.6-3.8 4.9-2.3.3-2.7-1-4.2-1s-2.4 1.4-4.6 1c-2.3-.5-2.3-3-3.6-4.9C2.4 12.6 3.6 11.7 3.3 9.5 3 7.2 5 6.1 7 5.6c2-.4 2.5-2.6 5-2.6Z"/></svg>',
    style:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h9"/><circle cx="17" cy="6" r="2.5"/><path d="M20 12h-9"/><circle cx="7" cy="12" r="2.5"/><path d="M4 18h8"/><circle cx="16" cy="18" r="2.5"/></svg>',
    dflt:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01"/><path d="M11 12h1v4h1"/></svg>'
  };
  /* the slot strip's own upload arrow, so an empty quarter reads as the same
     control the customer already met at the top of the lab */
  var UP = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>';
  var BAG = '<svg class="skc-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
  var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 12.5 5 5 10-11"/></svg>';
  /* the tile controls as glyphs (v2.6): eye = view, pencil = edit, x = remove */
  var EYE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>';
  var PEN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  var XG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  /* i18n: a row may carry its role as a third item (size, finish, quantity, designs, price,
     shape, style), so the icon does not depend on the language of its label. A row without
     one is read by its English label, as before. */
  var ICON_ROLE = { size: "size", finish: "finish", quantity: "quantity", designs: "designs", price: "price", total: "price", shape: "shape", style: "style",
    /* builder audit row 29 (31 Oct 2026): the bleed / border / colour choice as its own row */
    bleed: "bleed", border: "bleed", colour: "colour", color: "colour" };
  function tileIcon(label, role) {
    if (role && ICON_ROLE.hasOwnProperty(role)) return ICONS[ICON_ROLE[role]];
    var k = String(label || "").toLowerCase();
    if (k.indexOf("size") >= 0) return ICONS.size;
    if (k.indexOf("finish") >= 0) return ICONS.finish;
    if (k.indexOf("quantity") >= 0) return ICONS.quantity;
    if (k.indexOf("design") >= 0) return ICONS.designs;
    if (k.indexOf("price") >= 0 || k.indexOf("total") >= 0) return ICONS.price;
    if (k.indexOf("shape") >= 0) return ICONS.shape;
    if (k.indexOf("style") >= 0) return ICONS.style;
    return ICONS.dflt;
  }

  var esc = function (v) {
    return String(v == null ? "" : v).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  };

  /* ONE QUARTER. Filled: the proof, its actions, its own quantity, its name.
     Empty: the add tile. Both are the same cell, so the grid stays square. */
  /* v3.1 (Stickiply Studio, 24 Sep 2026): additive options, every other lab unchanged -
     o.tapToEdit() makes the proof itself the edit control (a phone, like the ORDER quarter),
     s.lim {min,max} greys the stepper at the pack's limits, and an add tile carries the
     product page's own count, "1 of 4 uploaded". */
  function cellHTML(s, o, filledCount, slotCount) {
    if (!s || s.empty) {
      return '<div class="lpc__cell lpc__cell--add" data-lpc-add="' + esc(s ? s.n : "") + '" role="button" tabindex="0">' +
        '<span class="lpc__slotn">' + esc(s ? s.n : "") + "</span>" +
        '<span class="lpc__addic">' + UP + "</span>" +
        '<span class="lpc__addtx"><span class="lpc__addt">' + esc(stkT("stk.studio.design.add", "Add a design")) + '</span>' +
        (slotCount ? '<span class="lpc__addn">' + esc(stkFmt(stkT("stk.studio.design.uploaded_count","[filled] of [total] uploaded"),{filled:filledCount,total:slotCount})) + "</span>" : "") +
        "</span></div>";
    }
    var id = String(s.id);
    var tapEdit = !!(o.onEditDesign && (typeof o.tapToEdit === "function" ? o.tapToEdit() : o.tapToEdit));
    var lim = s.lim || null, qv = s.qty == null ? 0 : Number(s.qty);
    return '<figure class="lpc__cell" style="margin:0" data-lpc-proof="' + esc(id) + '">' +
      '<span class="lpc__slotn">' + esc(s.n) + "</span>" +
      (tapEdit
        ? '<button type="button" class="lpc__proofimg" data-lpc-edit aria-label="'+esc(stkT("stk.studio.design.edit_aria","Edit this design"))+'">'
        : '<button type="button" class="lpc__proofimg" data-lpc-view aria-label="'+esc(stkT("stk.studio.confirm.view_larger_aria","View this proof larger"))+'">') +
      '<img src="' + esc(s.src) + '" alt="' + esc(s.caption || stkT("stk.studio.confirm.proof_alt", "Your proof")) + '"></button>' +
      /* v2.6: one line of 34px controls - eye, pencil, red x, then the stepper.
         The data hooks are unchanged, so every lab's wiring still works. */
      '<div class="lpc__ctl">' +
      '<button type="button" class="lpc__act" data-lpc-view aria-label="' + esc(stkT("stk.studio.confirm.view_larger_aria", "View this proof larger")) + '" title="' + esc(stkT("stk.studio.confirm.view_title", "View")) + '">' + EYE + "</button>" +
      (o.onEditDesign ? '<button type="button" class="lpc__act lpc__act--edit" data-lpc-edit aria-label="' + esc(stkT("stk.studio.design.edit_aria", "Edit this design")) + '" title="' + esc(stkT("stk.studio.confirm.edit_title", "Edit")) + '">' + PEN + "</button>" : "") +
      (o.onRemoveDesign ? '<button type="button" class="lpc__act lpc__act--rm" data-lpc-remove aria-label="' + esc(stkT("stk.studio.design.remove_aria", "Remove this design")) + '" title="' + esc(stkT("stk.studio.confirm.remove_title", "Remove")) + '"' +
        (filledCount > 1 ? "" : " disabled") + ">" + XG + "</button>" : "") +
      (o.onQty ? '<span class="lpc__q">' +
        '<button type="button" data-lpc-q="-1" aria-label="'+esc(stkT("stk.studio.design.qty_minus","One fewer"))+'"' + (lim && qv <= lim.min ? " disabled" : "") + '><svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>' +
        '<input type="number" min="' + esc(lim ? lim.min : 0) + '"' + (lim ? ' max="' + esc(lim.max) + '"' : "") + ' inputmode="numeric" data-lpc-qin value="' + esc(qv) + '" aria-label="'+esc(stkT("stk.studio.design.qty_input","Quantity of this design"))+'"' + (lim && lim.min === lim.max ? " readonly" : "") + ">" +
        '<button type="button" data-lpc-q="1" aria-label="'+esc(stkT("stk.studio.design.qty_plus","One more"))+'"' + (lim && qv >= lim.max ? " disabled" : "") + '><svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7h8M7 3v8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button></span>' : "") +
      "</div>" +
      /* 7 Oct 2026 (P212 audit 3): the words sit in their own span so a long caption ends in an ellipsis. Text straight
         inside the centred flex caption could not take text-overflow and was cut on BOTH sides, so a 375px phone lost the
         size at the start ("x 7.3 CM · NAME..."). The size comes first and now always shows; the name is what gets cut. */
      '<figcaption class="lpc__cap" title="' + esc(s.caption || "") + '"><span class="lpc__capt">' + esc(s.caption || stkT("stk.studio.confirm.proof_alt", "Your proof")) + "</span></figcaption>" +
      "</figure>";
  }
  function gridHTML(slots, o) {
    var filled = (slots || []).filter(function (s) { return s && !s.empty; }).length;
    var total = (slots || []).length, counted = false;
    /* the count goes on the first add tile only */
    return (slots || []).map(function (s) {
      var withCount = (!s || s.empty) && !counted && total > 1;
      if (withCount) counted = true;
      return cellHTML(s, o, filled, withCount ? total : 0);
    }).join("");
  }
  function tilesHTML(rows) {
    return (rows || []).map(function (r) {
      return '<div class="lpc__tile"><span class="lpc__ti">' + tileIcon(r[0], r[2]) + "</span>" +
        '<span class="lpc__tk">' + esc(r[0]) +
        '</span><span class="lpc__tv" title="' + esc(r[1]) + '">' + esc(r[1]) + "</span></div>";
    }).join("");
  }

  /* The product page's own Add to cart, cloned. Not restyled: every class,
     every inline custom property and the icon come across as they are. The
     hooks that let the theme's own scripts write into it are stripped, because
     this copy is outside the product form and must not be fed variant prices
     or a "Sold out" label by product-form.js / stk-buybtn.js. */
  function addButton(label) {
    var src = document.querySelector(
      'form[action*="/cart/add"] .product-form__submit, .product-form__submit, .shopify-payment-button__button'
    );
    var b;
    if (src && src.tagName === "BUTTON") {
      b = src.cloneNode(true);
      b.removeAttribute("id");
      b.removeAttribute("name");
      b.removeAttribute("disabled");
      b.removeAttribute("data-stkb");
      b.removeAttribute("data-section-id");
      b.removeAttribute("aria-haspopup");
      b.setAttribute("type", "button");
      var p = b.querySelectorAll(".stkb__price,.stkb__was,.loading__spinner,.loading-overlay__spinner");
      for (var i = 0; i < p.length; i++) p[i].parentNode.removeChild(p[i]);
      var sp = b.querySelector("span");
      if (sp) sp.textContent = label; else b.textContent = label;
    } else {
      b = document.createElement("button");
      b.setAttribute("type", "button");
      b.className = "product-form__submit button button--full-width button--primary";
      b.innerHTML = "<span>" + esc(label) + "</span>";
    }
    b.classList.add("lpc__add");
    return b;
  }

  var open = false;
  var node = null;

  /* WHAT THE CUSTOMER SEES ONCE THE LINE IS REALLY IN THE CART. The window's
     content is REPLACED, not covered (Thomas, 12 Sep 23:10). Tick, one line of
     fact, and the ways on: View cart, Close, and Add more designs only while
     there is a free slot. */
  function showDone(wrap, o) {
    var panel = wrap.querySelector(".lpc__panel");
    if (!panel) return;
    /* THE LINE COUNTS WHAT WENT ON THE ORDER. With four designs it used to read
       "Your design and its print files" beside a line carrying Artwork Upload
       (1) to (4). doneText may be a function so the lab counts at the moment the
       add succeeds, not at the moment the window opened - a design can be
       removed or added while it is on screen. */
    var line = typeof o.doneText === "function" ? o.doneText() : o.doneText;
    /* v2.9.2 (R10): ALWAYS LIGHT - the storefront has no dark mode - and the order summary */
    wrap.__lpcDone = true;
    wrap.removeAttribute("data-theme");
    wrap.classList.add("lpc--done");
    var sum = null;
    try { sum = typeof o.doneSummary === "function" ? o.doneSummary() : (o.doneSummary || null); } catch (err) { if (err) console.warn("[lab-proof-confirm] summary failed", err); }
    var thumbs = (sum && sum.thumbs || []).slice(0, 4).map(function (src) { return '<img class="lpc__donethumb" alt="" src="' + esc(src) + '">'; }).join("");
    var rowsS = (sum && sum.rows) || [];
    /* i18n: the total and the per-unit rows are found by the role the lab gives them (third
       item); a row without a role is read by its English label, as before */
    var totalRow = rowsS.filter(function (r) { return r[2] ? (r[2] === "price" || r[2] === "total") : /price|total/i.test(r[0]); })[0];
    var perRow = rowsS.filter(function (r) { return r[2] ? r[2] === "per" : /^per /i.test(r[0]); })[0];
    var stripRows = rowsS.filter(function (r) { return r !== totalRow && r !== perRow; });
    panel.innerHTML =
      '<div class="lpc__done">' +
      '<div class="lpc__donetick">' + TICK + "</div>" +
      '<p class="lpc__donet">' + esc(stkT("stk.studio.confirm.done_heading", "Added to your cart")) + '</p>' +
      '<p class="lpc__dones">' + esc(line || stkT("stk.studio.confirm.done_body", "Your design and its print files are on the order.")) + "</p>" +
      (thumbs ? '<div class="lpc__donethumbs">' + thumbs + "</div>" : "") +
      (stripRows.length ? '<div class="lpc__tiles">' + tilesHTML(stripRows) + "</div>" : "") +
      (totalRow ? '<div class="lpc__donetotal"><b>' + esc(totalRow[1]) + "</b>" + (perRow ? "<span>" + esc(perRow[3] != null ? perRow[3] : perRow[1] + " " + perRow[0].replace(/^per /i, "per ")) + "</span>" : "") + "</div>" : "") +
      '<div class="lpc__doneacts">' +
      /* i18n: the cart link keeps the shopper's language prefix (/fr/cart); "/" + "cart" in English */
      '<a class="skcb skcb--primary" href="' + esc(((window.Shopify && Shopify.routes && Shopify.routes.root) || "/") + "cart") + '">' + BAG + "<span>" + esc(stkT("stk.studio.confirm.view_cart", "View cart")) + "</span></a>" +
      '<button type="button" class="skcb skcb--secondary" data-lpc-close><span>' + esc(stkT("stk.studio.confirm.close", "Close")) + '</span></button>' +
      (o.canAddMore ? '<button type="button" class="skcb skcb--secondary" data-lpc-addmore-done><span>' + esc(stkT("stk.studio.confirm.add_more", "Add more designs")) + '</span></button>' : "") +
      "</div></div>";
  }

  function close() {
    if (node && node.parentNode) node.parentNode.removeChild(node);
    node = null;
    open = false;
    document.documentElement.style.removeProperty("overflow");
  }

  /* round 2 (owner 31 Oct): the opener Studio's bar controls (moon .plx-theme, cm/in [data-stk-units]) */
  function barCtl(sel) {
    var ps = document.querySelectorAll('.plx-host:not([hidden]) .plx-panel,.plx-panel--inline,.sslx-panel,.bmlx-panel,.gslx-panel');
    for (var i = 0; i < ps.length; i++) { var el = ps[i].querySelector(sel); if (el) return el; }
    return null;
  }
  function barHTML() {
    var th = barCtl(".plx-theme"), un = barCtl("[data-stk-units]"), h = "";
    if (un) {
      var lb = un.querySelectorAll(".stk-units__o");
      h += '<button type="button" class="lpc__units' + (un.classList.contains("on") ? " is-in" : "") + '" data-lpc-units role="switch" aria-checked="' + (un.classList.contains("on") ? "true" : "false") + '" aria-label="' + esc(un.getAttribute("aria-label") || "") + '">' +
        '<span class="lpc__uk" aria-hidden="true"></span><span class="lpc__uo">' + esc(lb[0] ? lb[0].textContent : "") + '</span><span class="lpc__uo">' + esc(lb[1] ? lb[1].textContent : "") + "</span></button>";
    }
    if (th) h += '<button type="button" class="lpc__th" data-lpc-theme aria-label="' + esc(th.getAttribute("aria-label") || th.getAttribute("title") || "") + '">' + th.innerHTML + "</button>";
    return h ? '<div class="lpc__bar">' + h + "</div>" : "";
  }
  function openOverlay(o) {
    o = o || {};
    css();
    close();
    open = true;
    acOn = false;
    acArmed = false;
    acCount = o.checkLines == null ? 1 : o.checkLines;   /* P240 v2: design lines this add makes */

    /* the caller may still speak v1's language (proofs only); four quarters are
       built from it so an older lab keeps working */
    var slots = o.slots;
    if (!slots) {
      slots = (o.proofs || []).map(function (pf, n) {
        return { n: n + 1, id: pf.id == null ? String(n) : String(pf.id), src: pf.src, caption: pf.caption, qty: pf.qty };
      });
    }
    var rows = o.rows || [];
    var wrap = document.createElement("div");
    wrap.className = "lpc";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-label", o.title || stkT("stk.studio.confirm.dialog_title", "Check your proof"));
    wrap.innerHTML =
      '<button type="button" class="lpc__scrim" data-lpc-close aria-label="' + esc(stkT("stk.studio.confirm.close", "Close")) + '" tabindex="-1"></button>' +
      '<div class="lpc__panel">' +
      '<div class="lpc__head">' +
      '<span class="lpc__hic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg></span>' +
      '<div class="lpc__ht">' +
      '<h2 class="lpc__t">' + esc(o.title || stkT("stk.studio.confirm.dialog_title", "Check your proof")) + "</h2>" +
      (o.lead ? '<p class="lpc__lead">' + esc(o.lead) + "</p>" : "") +
      "</div>" +
      barHTML() +
      '<button type="button" class="lpc__x" data-lpc-close aria-label="' + esc(stkT("stk.studio.confirm.close_edit_aria", "Close and keep editing")) + '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      "</button></div>" +
      '<div class="lpc__body">' +
      '<div class="lpc__proofs' + (slots.length === 1 ? " lpc__proofs--one" : "") + '">' + gridHTML(slots, o) + "</div>" +
      '<div class="lpc__tiles">' + tilesHTML(rows) + acHTML() + "</div>" +
      /* builder audit row 25: shown only while the body scrolls and its end is out of sight (lpcMore) */
      '<button type="button" class="lpc__more" aria-hidden="true" tabindex="-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></button>' +
      "</div>" +
      '<div class="lpc__foot">' +
      '<label class="lpc__confirm"><input type="checkbox" data-lpc-confirm checked>' +
      '<span class="lpc__box" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="m4.5 12.5 5 5 10-11"/></svg></span>' +
      "<span>" + esc(o.confirmText || stkT("stk.studio.confirm.rights", "I have checked this proof and I own the rights to this artwork.")) +
      "</span></label>" +
      /* v2.6 (item 1): download LEFT, Add to cart RIGHT, one row, equal
         heights. Without onDownload the slot is the full-width button, as
         every other lab expects. */
      (o.onDownload
        ? '<div class="lpc__row">' +
          '<div class="lpc__acts">' +
          '<button type="button" class="lpc__btn" data-lpc-download>' +
          '<span class="lpc__bic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><path d="m7 11 5 5 5-5"/><path d="M4 20h16"/></svg></span>' +
          '<span class="lpc__btx">' + esc(o.downloadLabel || stkT("stk.studio.confirm.download_files", "Download files")) +
          "<small>" + esc(o.downloadNote || stkT("stk.studio.confirm.download_note", "Print PNG + cut SVG + your proof, one ZIP")) + "</small></span></button>" +
          "</div>" +
          "<div data-lpc-slot></div>" +
          "</div>"
        : "<div data-lpc-slot></div>") +
      '<div class="lpc__prog" data-lpc-prog><div class="lpc__progtrack"><div class="lpc__progfill" data-lpc-progfill></div></div>' +
      '<div class="lpc__progcap" data-lpc-progcap></div></div>' +
      '<div class="lpc__status" data-lpc-status role="status" aria-live="polite"></div>' +
      "</div></div>";

    var btn = addButton(o.addLabel || stkT("stk.studio.add_to_cart", "Add to cart"));
    btn.disabled = true;
    wrap.querySelector("[data-lpc-slot]").appendChild(btn);

    var statusEl = wrap.querySelector("[data-lpc-status]");
    var tick = wrap.querySelector("[data-lpc-confirm]");
    var busy = false;
    /* P240: the artwork check box; the label makes the whole tile the control */
    wrap.addEventListener("change", function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute("data-lpc-chkin")) acOn = !!e.target.checked;
    });

    var progEl = wrap.querySelector("[data-lpc-prog]");
    var progFill = wrap.querySelector("[data-lpc-progfill]");
    var progCap = wrap.querySelector("[data-lpc-progcap]");
    function setStatus(msg, kind) {
      /* v2.9.2 (R9): while the bar is on there is ONE line under it - the caption. A busy or
         done message goes into the caption; an error takes the bar off and the status line. */
      var barOn = progEl && progEl.classList.contains("is-on");
      if (barOn && kind === "error") { progEl.className = "lpc__prog"; barOn = false; }
      if (barOn && msg) {
        progCap.textContent = msg;
        progCap.className = "lpc__progcap" + (kind ? " is-" + kind : "");
        statusEl.textContent = "";
        statusEl.className = "lpc__status";
        return;
      }
      statusEl.textContent = msg || "";
      statusEl.className = "lpc__status" + (kind ? " is-" + kind : "");
    }
    /* The lab drives this while it uploads: fraction 0..1, and which file of
       how many. Passed on setStatus so labs that do not use it are unaffected. */
    setStatus.progress = function (frac, done, total) {
      if (!progEl) return;
      if (frac == null) { progEl.className = "lpc__prog"; return; }
      var wasOff = !progEl.classList.contains("is-on");
      progEl.className = "lpc__prog is-on" + (frac >= 1 ? " is-done" : "");
      /* v2.9.3: a bar below the fold is a bar the customer does not have. It is brought
         into view the moment it turns on, once. */
      if (wasOff) { try { progEl.scrollIntoView({ block: "nearest" }); } catch (e) {} }
      /* v2.9.2 (R9): the fill is the larger of the bytes on the wire and the files finished, so
         the bar moves even when a PUT reports no upload progress events */
      var fr = Math.max(0, Math.min(1, Number(frac) || 0));
      if (total > 0 && done >= 0) fr = Math.max(fr, Math.min(1, done / total));
      progFill.style.width = Math.max(0, Math.min(100, Math.round(fr * 100))) + "%";
      progCap.className = "lpc__progcap";
      /* v2.9 (additive): a lab may word the caption itself, in files -
         "Uploading 7 of 12 files" - and keep its own status line above the bar.
         Without the options the caption and the blanking are as before. */
      progCap.textContent = (typeof o.progressCaption === "function")
        ? String(o.progressCaption(done, total, frac) || "")
        : ((frac >= 1)
          ? stkT("stk.studio.confirm.files_sent", "Files sent")
          : (total ? fmt(stkT("stk.studio.confirm.uploading_n", "Uploading [n] of [total]"), { n: Math.min(done + 1, total), total: total }) : stkT("stk.studio.confirm.uploading", "Uploading")));
      /* the bar replaces the sentence while it runs (v2.9.2: always, one line under the bar) */
      statusEl.textContent = ""; statusEl.className = "lpc__status";
    };
    function sync() {
      btn.disabled = busy || !tick.checked;
    }
    tick.addEventListener("change", sync);
    sync();   // ticked by default (12 Sep 18:05), so the button starts live

    /* ONE REDRAW for the grid and the tiles. A removal renumbers every design,
       and a quantity typed on one tile re-splits the others, so both come back
       from the lab as a whole payload rather than a single figure. */
    function apply(next) {
      if (!next) return;
      if (next.slots) {
        slots = next.slots;
        var pg = wrap.querySelector(".lpc__proofs");
        pg.innerHTML = gridHTML(slots, o);
        pg.classList.toggle("lpc__proofs--one", slots.length === 1);
      }
      if (next.rows) {
        rows = next.rows;
        wrap.querySelector(".lpc__tiles").innerHTML = tilesHTML(rows) + acHTML();
      }
      /* a quantity typed on ONE tile re-splits the others, so every tile is
         told its new figure - no proof is re-rendered for that */
      if (next.qtys) {
        Object.keys(next.qtys).forEach(function (k) {
          var el = wrap.querySelector('[data-lpc-proof="' + k + '"] [data-lpc-qin]');
          if (el && String(el.value) !== String(next.qtys[k])) el.value = next.qtys[k];
          for (var i = 0; i < slots.length; i++) if (slots[i] && String(slots[i].id) === k) slots[i].qty = next.qtys[k];
        });
      }
      /* v3.1: the stepper's limits follow every re-split */
      if (next.limits) {
        Object.keys(next.limits).forEach(function (k) {
          var fig = wrap.querySelector('[data-lpc-proof="' + k + '"]'), L = next.limits[k];
          if (!fig || !L) return;
          var inp = fig.querySelector("[data-lpc-qin]"), v = inp ? Number(inp.value) || 0 : 0;
          var mi = fig.querySelector('[data-lpc-q="-1"]'), pl = fig.querySelector('[data-lpc-q="1"]');
          if (mi) mi.disabled = v <= L.min;
          if (pl) pl.disabled = v >= L.max;
          if (inp) { inp.min = L.min; inp.max = L.max; inp.readOnly = L.min === L.max; }
          for (var i = 0; i < slots.length; i++) if (slots[i] && String(slots[i].id) === k) slots[i].lim = L;
        });
      }
      if (typeof next.canAddMore === "boolean") o.canAddMore = next.canAddMore;
      /* v2.5: a removal takes a row out and an add puts one back, so the height
         the cells may take has changed - re-fit before the customer sees it */
      if (wrap.__lpcFit) wrap.__lpcFit();
    }

    var dl = wrap.querySelector("[data-lpc-download]");
    if (dl) dl.addEventListener("click", function () {
      if (busy) return;
      dl.disabled = true;
      setStatus(stkT("stk.studio.confirm.building", "Building your files..."), "busy");
      Promise.resolve().then(function () { return o.onDownload(); }).then(function () {
        setStatus(o.downloadDoneText || stkT("stk.studio.confirm.downloading", "Your files are downloading"), "done");
        /* v2.9 (additive): with downloadDoneMs the done line fades and clears once the
           ZIP has been handed to the browser, instead of staying on screen */
        if (o.downloadDoneMs > 0) {
          var tok = (wrap.__lpcDlTok = (wrap.__lpcDlTok || 0) + 1);
          setTimeout(function () {
            if (wrap.__lpcDlTok !== tok || !statusEl.classList.contains("is-done")) return;
            statusEl.classList.add("is-fading");
            setTimeout(function () { if (wrap.__lpcDlTok === tok && statusEl.classList.contains("is-fading")) setStatus("", null); }, 450);
          }, o.downloadDoneMs);
        }
      }).catch(function (err) {
        if (err) console.warn("[lab-proof-confirm] download failed", err);
        setStatus(stkT("stk.studio.confirm.build_failed", "Could not build your files. Try again."), "error");
      }).then(function () { dl.disabled = false; });
    });

    function bigView(img) {
      var big = document.createElement("div");
      big.className = "lpc__big";
      big.innerHTML = '<img src="' + esc(img.getAttribute("src")) + '" alt="">';
      wrap.appendChild(big);
    }

    /* a tile's stepper: the lab owns the split, so it is told the new figure
       and hands back every design's share */
    function setQty(fig, value) {
      if (!o.onQty) return;
      var id = fig.getAttribute("data-lpc-proof");
      var n = Math.max(0, Math.round(Number(value) || 0));
      try { apply(o.onQty(id, n)); } catch (err) { if (err) console.warn("[lab-proof-confirm] qty failed", err); }
    }

    wrap.addEventListener("change", function (e) {
      var qin = e.target.closest("[data-lpc-qin]");
      if (!qin || busy) return;
      setQty(qin.closest("[data-lpc-proof]"), qin.value);
    });

    wrap.addEventListener("click", function (e) {
      /* the success screen's own buttons still work while nothing else does */
      if (e.target.closest("[data-lpc-addmore-done]")) {
        close();
        try { if (o.onAddMoreDone) o.onAddMoreDone(); } catch (err) { if (err) console.warn("[lab-proof-confirm] add more failed", err); }
        return;
      }
      if (e.target.closest("[data-lpc-close]")) {
        if (busy) return;
        var wasDone = !!wrap.__lpcDone;
        close();
        if (wasDone) { try { if (typeof o.onDoneClose === "function") o.onDoneClose(); } catch (err) { if (err) console.warn("[lab-proof-confirm] done close failed", err); } return; }
        if (typeof o.onBack === "function") o.onBack();
        return;
      }
      /* v2.9.2 (R10): VIEW CART is a link to /cart; the lab closes behind it so the page is not locked if the customer comes back */
      if (e.target.closest(".lpc__doneacts a[href]")) { try { if (typeof o.onDoneClose === "function") o.onDoneClose(); } catch (err) {} return; }
      if (busy) return;
      /* the big view closes on any click */
      var big = e.target.closest(".lpc__big");
      if (big) { big.parentNode.removeChild(big); return; }

      var addCell = e.target.closest("[data-lpc-add]");
      if (addCell) {
        var n = Number(addCell.getAttribute("data-lpc-add"));
        close();
        try { if (o.onAddSlot) o.onAddSlot(n); } catch (err) { if (err) console.warn("[lab-proof-confirm] add slot failed", err); }
        return;
      }

      var q = e.target.closest("[data-lpc-q]");
      if (q && q.disabled) return;
      if (q) {
        var figq = q.closest("[data-lpc-proof]");
        var inp = figq.querySelector("[data-lpc-qin]");
        setQty(figq, (Number(inp.value) || 0) + Number(q.getAttribute("data-lpc-q")));
        return;
      }

      var act = e.target.closest("[data-lpc-view],[data-lpc-edit],[data-lpc-remove]");
      if (act) {
        if (act.hasAttribute("disabled")) return;
        var fig = act.closest("[data-lpc-proof]");
        var id = fig.getAttribute("data-lpc-proof");
        if (act.hasAttribute("data-lpc-view")) { bigView(fig.querySelector("img")); return; }
        if (act.hasAttribute("data-lpc-edit")) {
          close();
          try { o.onEditDesign(id); } catch (err) { if (err) console.warn("[lab-proof-confirm] edit failed", err); }
          return;
        }
        if (act.hasAttribute("data-lpc-remove")) {
          /* plain words, and only once */
          if (!window.confirm(stkT("stk.studio.confirm.remove_ask", "Take this design out of the order? Its files are not uploaded yet."))) return;
          /* the window STAYS OPEN: the quarter it leaves behind becomes an
             add-a-design tile (Thomas, 12 Sep 23:05) */
          try { apply(o.onRemoveDesign(id)); } catch (err) { if (err) console.warn("[lab-proof-confirm] remove failed", err); }
          return;
        }
      }
    });
    document.addEventListener("keydown", function onKey(e) {
      if (!open) { document.removeEventListener("keydown", onKey); return; }
      if (e.key === "Escape" && !busy) { var wd = !!(node && node.__lpcDone); close(); if (wd) { try { if (typeof o.onDoneClose === "function") o.onDoneClose(); } catch (err) {} } else if (typeof o.onBack === "function") o.onBack(); }
      /* v2.8: Tab and Shift+Tab move through the window's own controls and
         never onto the page behind it. Handled in full rather than only at
         the ends: WebKit's native Tab skips buttons (measured 17 Sep), so the
         browser's next stop after a tile was the page behind the veil. */
      if (e.key === "Tab") {
        var f = Array.prototype.filter.call(wrap.querySelectorAll('button:not([disabled]),[href],input:not([disabled]),[tabindex]:not([tabindex="-1"])'),
          function (el) { return el.getAttribute("tabindex") !== "-1" && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); });
        if (!f.length) return;
        var a = document.activeElement, i = f.indexOf(a), n = f.length;
        e.preventDefault();
        (e.shiftKey ? (i <= 0 ? f[n - 1] : f[i - 1]) : (i < 0 || i >= n - 1 ? f[0] : f[i + 1])).focus();
      }
    });

    btn.addEventListener("click", function () {
      if (busy || !tick.checked) return;
      busy = true;
      sync();
      tick.disabled = true;
      acArmed = acOn && !!acCfg();
      var acIn = wrap.querySelector("[data-lpc-chkin]");
      if (acIn) acIn.disabled = true;
      setStatus(o.addingText || stkT("stk.studio.confirm.adding", "Adding to your cart..."), "busy");
      var ret;
      try {
        ret = typeof o.onAdd === "function" ? o.onAdd(setStatus, { artworkCheck: acArmed }) : null;
      } catch (err) {
        ret = Promise.reject(err);
      }
      Promise.resolve(ret).then(function (ok) {
        if (ok === false) throw new Error("");
        acArmed = false;
        /* THE OVERLAY MUST LET GO. It used to leave busy true, and busy is what
           the close handler and the Escape key check, so the panel could never
           be dismissed after a successful add. */
        busy = false;
        /* v3.1: a lab may skip the success card (Stickiply Studio on a phone): the window lets
           go and the cart drawer the add opened is what the customer sees */
        if (typeof o.skipDone === "function" ? o.skipDone() : o.skipDone) {
          close();
          try { if (typeof o.onDoneClose === "function") o.onDoneClose(); } catch (err) { if (err) console.warn("[lab-proof-confirm] done close failed", err); }
          return;
        }
        showDone(wrap, o);
      }).catch(function (err) {
        busy = false;
        tick.disabled = false;
        acArmed = false;
        var acIn2 = wrap.querySelector("[data-lpc-chkin]");
        if (acIn2) acIn2.disabled = false;
        sync();
        // Plain words only. Anything that looks like developer text is swapped for the
        // fallback by LabCart.plainError; the real error is already in the console.
        var LC = window.LabCart;
        setStatus(
          LC && LC.plainError
            ? LC.plainError(err, stkT("stk.studio.msg.add_failed", "Could not add to the cart. Try again."))
            : stkT("stk.studio.msg.add_failed", "Could not add to the cart. Try again."),
          "error"
        );
      });
    });

    /* THE MODAL FOLLOWS THE LAB'S THEME (Thomas, 12 Sep 22:35). It is appended
       to the body, outside the launcher panel that carries the flag, so the
       flag is mirrored onto it and kept in step while it is open. */
    function syncTheme() {
      if (wrap.__lpcDone) { wrap.removeAttribute("data-theme"); return; }   /* v2.9.2 (R10): the success card is always light */
      var panel = document.querySelector('.plx-panel[data-theme="dark"],.sslx-panel[data-theme="dark"],.bmlx-panel[data-theme="dark"],.gslx-panel[data-theme="dark"]');
      if (panel) wrap.setAttribute("data-theme", "dark"); else wrap.removeAttribute("data-theme");
    }
    syncTheme();
    /* round 2: the head's moon and cm/in mirror the Studio bar; a press is the bar's own press */
    function syncBar() {
      var th = barCtl(".plx-theme"), un = barCtl("[data-stk-units]");
      var mt = wrap.querySelector("[data-lpc-theme]"), mu = wrap.querySelector("[data-lpc-units]");
      if (mt && th) { mt.innerHTML = th.innerHTML; mt.setAttribute("aria-label", th.getAttribute("aria-label") || th.getAttribute("title") || ""); }
      if (mu && un) { var on = un.classList.contains("on"); mu.classList.toggle("is-in", on); mu.setAttribute("aria-checked", on ? "true" : "false"); }
    }
    wrap.addEventListener("click", function (e) {
      var b = e.target.closest && e.target.closest("[data-lpc-theme],[data-lpc-units]");
      if (!b) return;
      var src = barCtl(b.hasAttribute("data-lpc-theme") ? ".plx-theme" : "[data-stk-units]");
      if (src) { try { src.click(); } catch (err) {} }
      setTimeout(syncBar, 0);
    });
    document.addEventListener("stk:lab-theme", function onTheme() {
      if (!open || !wrap.isConnected) { document.removeEventListener("stk:lab-theme", onTheme); return; }
      setTimeout(function () { syncTheme(); syncBar(); }, 0);
    });
    document.addEventListener("stk:lab-units", function onUnits() {
      if (!open || !wrap.isConnected) { document.removeEventListener("stk:lab-units", onUnits); return; }
      setTimeout(function () {
        syncBar();
        if (typeof o.onUnits === "function") { try { apply(o.onUnits()); } catch (err) { console.warn("[lab-proof-confirm] units redraw failed", err); } }
      }, 0);
    });

    /* ===== THE WINDOW FITS THE VIEWPORT (v2.5) =========================
       Measured on v2.4 at 1440x950: .lpc__body clientHeight 632, scrollHeight
       818. The proof boxes are the only elastic thing in the window, so they
       are the thing that gives: shrink them until the body no longer scrolls,
       down to a floor where a proof stops being a proof. Head, tiles, tick,
       ADD TO CART and the download group all stay inside the window.
       Re-run on resize, on a visual-viewport change (phone chrome), and after
       the grid is re-rendered by apply(). */
    var IMG_MAX = 200, IMG_MIN = 84;
    /* builder audit row 25 (31 Oct 2026): a body that still scrolls after fit() says so */
    function lpcMore() {
      var body = wrap.querySelector(".lpc__body"); if (!body) return;
      var more = body.scrollHeight - body.clientHeight > 2 && body.scrollTop + body.clientHeight < body.scrollHeight - 8;
      wrap.classList.toggle("lpc--scrolls", body.scrollHeight - body.clientHeight > 2);
      wrap.classList.toggle("lpc--more", more);
      if (!body.__lpcMore) {
        body.__lpcMore = true;
        body.addEventListener("scroll", lpcMore, { passive: true });
        var mb = body.querySelector(".lpc__more");
        if (mb) mb.addEventListener("click", function () { try { body.scrollBy({ top: Math.round(body.clientHeight * 0.8), behavior: "smooth" }); } catch (e) { body.scrollTop += Math.round(body.clientHeight * 0.8); } });
      }
    }
    function fit() { fit0(); lpcMore(); }
    function fit0() {
      var body = wrap.querySelector(".lpc__body"), grid = wrap.querySelector(".lpc__proofs");
      if (!body) return;
      var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 0;
      /* The lab's shell measures any fixed bar pinned to the foot of the page
         (Shopify's theme-preview bar, a cookie bar) and publishes it as
         --plx-inset. The proof window opens over the same page, so it clears
         the same bar: the overlay's own bottom edge lifts by the inset and the
         panel is sized to what is left. 0 anywhere that bar does not exist. */
      var inset = 0;
      try { inset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--plx-inset")) || 0; } catch (e) { inset = 0; }
      if (inset > 0 && inset < vh * 0.4) wrap.style.bottom = Math.round(inset) + "px";
      else { wrap.style.bottom = ""; inset = 0; }
      if (vh) wrap.style.setProperty("--lpc-vh", Math.round(vh - inset) + "px");
      var small = (window.innerWidth || 0) <= 600;
      var max = small ? 150 : IMG_MAX, min = small ? 72 : IMG_MIN;
      wrap.classList.remove("lpc--compact");
      var cells = wrap.querySelectorAll(".lpc__cell").length || 4;
      /* try the 2x2 square first; if the shortest proof still will not fit -
         measured at 1280x720, where two rows of quarters need 538px inside
         402px - lay the quarters out in ONE row instead. A shorter proof in a
         window that fits beats a taller proof under the fold. */
      function attempt(cols) {
        cols = Math.max(1, Math.min(cols, cells));   // one design never gets half a window
        if (grid) grid.style.gridTemplateColumns = "repeat(" + cols + ",minmax(0,1fr))";
        var rowsN = Math.max(1, Math.ceil(cells / cols));
        var h = max;
        wrap.style.setProperty("--lpc-imgh", h + "px");
        for (var k = 0; k < 18; k++) {
          var over = body.scrollHeight - body.clientHeight;
          if (over <= 0) return true;
          h = Math.max(min, h - (Math.ceil(over / rowsN) + 1));
          wrap.style.setProperty("--lpc-imgh", h + "px");
          if (h <= min) break;
        }
        return body.scrollHeight <= body.clientHeight;
      }
      /* v2.8 (item 4): the six controls sit on ONE line, so a cell is never
         narrower than the 252px row. A phone gets one column (the body
         scrolls, the foot stays); a desktop keeps two columns, and a short
         window (1280x720) drops the lead and tightens the gaps rather than
         falling back to four 172px cells whose controls wrapped. */
      if (small) { attempt(1); return; }
      if (attempt(2)) return;
      wrap.classList.add("lpc--compact");
      min = 64;
      attempt(2);
    }
    var _fitRaf = 0;
    function fitSoon() {
      if (_fitRaf) return;
      _fitRaf = requestAnimationFrame(function () {
        _fitRaf = 0;
        /* a closed window's listeners stop working rather than resize a node
           that is no longer in the document */
        if (node !== wrap) {
          window.removeEventListener("resize", fitSoon);
          if (window.visualViewport) window.visualViewport.removeEventListener("resize", fitSoon);
          return;
        }
        fit();
      });
    }
    wrap.__lpcFit = fitSoon;
    window.addEventListener("resize", fitSoon);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", fitSoon);

    document.body.appendChild(wrap);
    node = wrap;
    fit();
    fitSoon();
    try { tick.focus({ preventScroll: true }); } catch (e) {}
    return { setStatus: setStatus, close: close };
  }

  window.LabProofConfirm = {
    open: openOverlay,
    close: close,
    isOpen: function () { return open; },
    /* P240 artwork check: state at the last Add press, line builder, rollback */
    artworkCheck: function () { return acArmed; },
    withCheck: acLines,
    dropChecks: acDrop
  };
})();
